import { Checkbox } from '@qr-front/ui/components/Checkbox'
import type { SortingFnOption } from '@tanstack/react-table'
import { createColumnHelper } from '@tanstack/react-table'

import { TQrData } from '@/generator/shared'

const localeSortKey = 'localeSort' as SortingFnOption<TQrData>
const scansNumericSortKey = 'scansNumericSort' as SortingFnOption<TQrData>
const typeSortKey = 'typeSort' as SortingFnOption<TQrData>

const columnHelper = createColumnHelper<TQrData>()

export const selectColDef = columnHelper.display({
	id: 'select',
	cell: ({ row }) => (
		<Checkbox
			id="select"
			checked={row.getIsSelected()}
			onCheckedChange={(value) => row.toggleSelected(!!value)}
		/>
	),
})

export const previewColDef = columnHelper.accessor(
	(row) => ({
		id: row.id,
		qrDesign: row.qrDesign,
		content: row.content,
		contentType: row.content.type,
		uri: row.uri,
		disabled: row.disabled,
		userLanguage: row.userLanguage,
		createdAt: row.createdAt,
		updatedAt: row.updatedAt,
		name: row.name,
		scansAmount: row.scansAmount,
		autoDisabled: row.autoDisabled,
		hasPassword: row.hasPassword,
	}),
	{
		id: 'preview',
		filterFn: (row, _, filterValue: string[]) => {
			if (!filterValue || filterValue.length === 0) return true
			const value = row.original.content.type
			return filterValue.includes(value)
		},
	},
)

export const nameColDef = columnHelper.accessor('name', {
	id: 'name',
	sortingFn: localeSortKey,
	filterFn: (row, _, filterValue: string) => {
		if (!filterValue) return true
		const searchValue = filterValue.toLowerCase()
		const name = row.original.name?.toLowerCase() || ''

		if (name.includes(searchValue)) return true

		const content = row.original.content as { type: string; url?: string }
		if (content.type === 'url' && content.url) {
			const websiteUrl = content.url.toLowerCase()
			return websiteUrl.includes(searchValue)
		}

		return false
	},
})

export const uriColDef = columnHelper.accessor('uri', {
	id: 'uri',
})

export const typeColDef = columnHelper.accessor('content.type', {
	id: 'type',
	sortingFn: typeSortKey,
})

export const scansColDef = columnHelper.accessor('scansAmount', {
	id: 'scans',
	sortingFn: scansNumericSortKey,
})

export const disabledColDef = columnHelper.accessor('disabled', {
	id: 'disabled',
	sortingFn: 'basic',
	filterFn: (row, _, filterValue: string[]) => {
		if (!filterValue || filterValue.length === 0) return true
		const value = row.original.disabled
		return filterValue.includes(value.toString())
	},
})

export const createdColDef = columnHelper.accessor('createdAt', {
	id: 'created',
	sortingFn: 'datetime',
})

export const editDateColDef = columnHelper.accessor('updatedAt', {
	id: 'edited',
	sortingFn: 'datetime',
})

export const modifiedColDef = columnHelper.accessor('updatedAt', {
	id: 'modified',
	sortingFn: 'datetime',
})

export const columns = [
	selectColDef,
	previewColDef,
	nameColDef,
	uriColDef,
	typeColDef,
	createdColDef,
	editDateColDef,
	modifiedColDef,
	scansColDef,
	disabledColDef,
]
